import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import {
  Grid,
  Button,
  useMediaQuery,
  useTheme,
  Typography,
  Menu,
  MenuItem,
  Box,
  Checkbox,
  FormControlLabel,
} from '@mui/material'

// styles
import { useTranslation } from 'react-i18next'
import useStyles from './styles'

// components
import Widget from '../../components/Widget'
import PageTitle from '../../components/PageTitle'
import { useDataApi } from '../../context/DataApiContext'
import Loading from '../../components/Loading'
import CreateVehicleTypeDialog from '../../components/VehicleType/components/CreateVehicleTypeDialog'
import { useNotificationsProvider } from '../../context/NotificationsContext'
import CreateChassisDialog from '../../components/Chassis/components/CreateChassisDialog'
import CreateChassisBulkDialog from '../../components/Chassis/components/CreateChassisBulkDialog'
import CreateVehicleTypeBulkDialog from '../../components/VehicleType/components/CreateVehicleTypeBulkDialog'
import GeneratePVAdmissionDialog from '../../components/Chassis/components/GeneratePVAdmissionDialog'
import ChassisDocumentImportDialog from '../../components/Chassis/components/ChassisDocumentImportDialogWrapper'
import CreateControlDialog from '../../components/Control/components/CreateControlDialog'
import { hasPermission, useUserState } from '../../context/user/UserContext'
import { Permissions } from '../../entities/permissions'
import { useExport } from '../../context/ExportContext'
import BFTableInfoCard from '../../components/table-info-card'
import InspectionsCalendar from '../../components/inspections-calendar'
import CalendarImport from '../../components/CalendarImport'
import BaseDialog from '../../components/BaseDialog'
import ControlDetailsPreview from '../../components/ControlDetailsPreview'
import generateColumns from './columns'
import generateValidatedWithTotalControlCountColumns from './validated-with-total-control-count-columns'
import generateInterruptedControlsColumns from './interrupted-controls-columns'
import { useQueryState } from '../../utils/query-state'
import DatePicker from '../../components/DatePicker'
import AutoCompleteInput from '../../components/AutoCompleteInput'
import { toLocalISOString } from '../../utils/formatters'

function useQuery() {
  const { search } = useLocation()

  return useMemo(() => new URLSearchParams(search), [search])
}

export default function Dashboard({ history }) {
  const classes = useStyles()
  const { t } = useTranslation()
  const theme = useTheme()
  const fullWidth = useMediaQuery(theme.breakpoints.down('sm'))
  const { dataProvider } = useDataApi()
  const { showNotification } = useNotificationsProvider()
  const { user } = useUserState()
  const { startExport } = useExport()

  const canAddChassis = hasPermission(
    Permissions.PAGE_DASHBOARD_ADD_CHASSIS,
    user
  )
  const canAddChassisDocuments = hasPermission(
    Permissions.PAGE_CHASSIS_ADD_DOCUMENTS,
    user
  )
  const addChassisButtonLabel = () => {
    if (canAddChassis && canAddChassisDocuments) {
      return t('dashboard-view.add-chassis')
    }
    if (canAddChassis) {
      return t('dashboard-view.add-chassis-only')
    }
    return t('dashboard-view.add-documents-only')
  }

  const [failedLoad, setFailedLoad] = useState(false)
  const [counts, setCounts] = useState(null)
  const [openCreateVehicleTypeDialog, setOpenCreateVehicleTypeDialog] =
    useState(false)
  const [openCreateChassisDialog, setOpenCreateChassisDialog] = useState(false)
  const [openCreateChassisBulkDialog, setOpenCreateChassisBulkDialog] =
    useState(false)
  const [openChassisDocumentImportDialog, setOpenChassisDocumentImportDialog] =
    useState(false)
  const [openCreateVehicleTypeBulkDialog, setOpenCreateVehicleTypeBulkDialog] =
    useState(false)
  const [openGeneratePVAdmissionDialog, setOpenGeneratePVAdmissionDialog] =
    useState(false)
  const [openCreateControlDialog, setOpenCreateControlDialog] = useState(false)
  const [createControlDefaultDate, setCreateControlDefaultDate] = useState(null)
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const calendarRefreshRef = useRef(null)
  const [exportTemplateDialogOpen, setExportTemplateDialogOpen] =
    useState(false)
  const [exportTemplateConstructor, setExportTemplateConstructor] =
    useState(null)
  const [exportTemplateLoading, setExportTemplateLoading] = useState(false)

  const [addActionsAnchorEl, setAddActionsAnchorEl] = useState({
    anchorEl: null,
    items: [],
  })

  // Filters
  const query = useQuery()

  // Set start date to 1 year ago
  const [startDate, setStartDate] = useQueryState({
    paramName: 'startDate',
    history,
    query,
    fromQuery: useCallback(
      (value) =>
        value
          ? new Date(value)
          : new Date(new Date().setMonth(new Date().getMonth() - 12)),
      []
    ),
    toQuery: useCallback((value) => toLocalISOString(value), []),
  })
  const [endDate, setEndDate] = useQueryState({
    paramName: 'endDate',
    history,
    query,
    fromQuery: useCallback(
      (value) => (value ? new Date(value) : new Date()),
      []
    ),
    toQuery: useCallback((value) => toLocalISOString(value), []),
  })
  const [vehicleTypes, setVehicleTypes] = useQueryState({
    paramName: 'vehicleTypes',
    history,
    query,
  })
  const [constructors, setConstructors] = useQueryState({
    paramName: 'constructors',
    history,
    query,
  })
  const [member, setMember] = useQueryState({
    paramName: 'member',
    history,
    query,
  })
  const [showInactive, setShowInactive] = useQueryState({
    paramName: 'showInactive',
    history,
    query,
  })

  // Calendar state persistence
  const [calendarView, setCalendarView] = useQueryState({
    paramName: 'calendarView',
    history,
    query,
    fromQuery: useCallback((value) => value || 'dayGridMonth', []),
    toQuery: useCallback((value) => value, []),
  })
  const [calendarDate, setCalendarDate] = useQueryState({
    paramName: 'calendarDate',
    history,
    query,
    fromQuery: useCallback((value) => (value ? new Date(value) : null), []),
    toQuery: useCallback(
      (value) => (value ? toLocalISOString(value) : null),
      []
    ),
  })
  const [calendarControllers, setCalendarControllers] = useQueryState({
    paramName: 'calendarControllers',
    history,
    query,
    fromQuery: useCallback((value) => {
      if (!value) return null // No URL param - use default
      if (value === 'all') return [] // Explicitly "show all" (no filter)
      return value
        .split(',')
        .filter(Boolean)
        .map((id) => {
          // Keep special IDs as strings, convert numeric IDs to numbers
          if (id === '__unassigned__' || id === '__select_all__') {
            return id
          }
          const num = parseInt(id, 10)
          return isNaN(num) ? id : num
        })
    }, []),
    toQuery: useCallback((value) => {
      if (value === null || value === undefined) return null
      if (value.length === 0) return 'all' // Explicitly "show all" (no filter)
      return value.join(',')
    }, []),
  })

  const computedFilters = useMemo(() => {
    const filtersObj = {
      startDate: startDate instanceof Date ? toLocalISOString(startDate) : null,
      endDate: endDate instanceof Date ? toLocalISOString(endDate) : null,
      vehicleTypeIds:
        vehicleTypes && vehicleTypes.length
          ? vehicleTypes.map((vt) => vt.id)
          : null,
      constructorIds:
        constructors && constructors.length
          ? constructors.map((c) => c.id)
          : null,
      memberId: member ? member.id : null,
      showInactive: showInactive === '1' ? true : null,
    }
    // remove nulls
    Object.keys(filtersObj).forEach(
      (key) => filtersObj[key] === null && delete filtersObj[key]
    )

    return filtersObj
  }, [startDate, endDate, vehicleTypes, constructors, member, showInactive])

  // 1st control Vehicle Type Table
  const [
    vehiclesWaitingFirstControlByVehicleTypeTablePage,
    setVehiclesWaitingFirstControlByVehicleTypeTablePage,
  ] = useQueryState({
    paramName: 'vehiclesWaitingFirstControlByVehicleTypePage',
    defaultValue: 0,
    history,
    query,
  })

  const [
    vehiclesWaitingFirstControlByVehicleTypeTableState,
    setVehiclesWaitingFirstControlByVehicleTypeTableState,
  ] = useState({
    page: vehiclesWaitingFirstControlByVehicleTypeTablePage,
    limit: 5,
    filters: {
      ...computedFilters,
      groupBy: 'vehicleType',
    },
  })
  const [
    vehiclesWaitingFirstControlByVehicleTypeTableData,
    setVehiclesWaitingFirstControlByVehicleTypeTableData,
  ] = useState(null)

  // 1st control Constructor Table
  const [
    vehiclesWaitingFirstControlByConstructorTablePage,
    setVehiclesWaitingFirstControlByConstructorTablePage,
  ] = useQueryState({
    paramName: 'vehiclesWaitingFirstControlByConstructorPage',
    defaultValue: 0,
    history,
    query,
  })
  const [
    vehiclesWaitingFirstControlByConstructorTableState,
    setVehiclesWaitingFirstControlByConstructorTableState,
  ] = useState({
    page: vehiclesWaitingFirstControlByConstructorTablePage,
    limit: 5,
    filters: {
      ...computedFilters,
      groupBy: 'constructor',
    },
  })
  const [
    vehiclesWaitingFirstControlByConstructorTableData,
    setVehiclesWaitingFirstControlByConstructorTableData,
  ] = useState(null)

  // Add state for selected view persistence
  const [
    vehiclesWaitingFirstControlSelectedView,
    setVehiclesWaitingFirstControlSelectedView,
  ] = useQueryState({
    paramName: 'vehiclesWaitingFirstControlSelectedView',
    defaultValue: 0,
    history,
    query,
  })

  // Recontrol Vehicle Type Table
  const [
    vehiclesWaitingRecontrolByVehicleTypeTablePage,
    setVehiclesWaitingRecontrolByVehicleTypeTablePage,
  ] = useQueryState({
    paramName: 'vehiclesWaitingRecontrolByVehicleTypePage',
    defaultValue: 0,
    history,
    query,
  })

  const [
    vehiclesWaitingRecontrolByVehicleTypeTableState,
    setVehiclesWaitingRecontrolByVehicleTypeTableState,
  ] = useState({
    page: vehiclesWaitingRecontrolByVehicleTypeTablePage,
    limit: 5,
    filters: {
      ...computedFilters,
      groupBy: 'vehicleType',
    },
  })
  const [
    vehiclesWaitingRecontrolByVehicleTypeTableData,
    setVehiclesWaitingRecontrolByVehicleTypeTableData,
  ] = useState(null)

  // Recontrol Constructor Table
  const [
    vehiclesWaitingRecontrolByConstructorTablePage,
    setVehiclesWaitingRecontrolByConstructorTablePage,
  ] = useQueryState({
    paramName: 'vehiclesWaitingRecontrolByConstructorPage',
    defaultValue: 0,
    history,
    query,
  })
  const [
    vehiclesWaitingRecontrolByConstructorTableState,
    setVehiclesWaitingRecontrolByConstructorTableState,
  ] = useState({
    page: vehiclesWaitingRecontrolByConstructorTablePage,
    limit: 5,
    filters: {
      ...computedFilters,
      groupBy: 'constructor',
    },
  })
  const [
    vehiclesWaitingRecontrolByConstructorTableData,
    setVehiclesWaitingRecontrolByConstructorTableData,
  ] = useState(null)

  // Add state for selected view persistence for recontrol
  const [
    vehiclesWaitingRecontrolSelectedView,
    setVehiclesWaitingRecontrolSelectedView,
  ] = useQueryState({
    paramName: 'vehiclesWaitingRecontrolSelectedView',
    defaultValue: 0,
    history,
    query,
  })

  // Add state for Vehicles OK or Reservations by Vehicle Type
  const [
    vehiclesOkOrReservationsByVehicleTypeTablePage,
    setVehiclesOkOrReservationsByVehicleTypeTablePage,
  ] = useQueryState({
    paramName: 'vehiclesOkOrReservationsByVehicleTypePage',
    defaultValue: 0,
    history,
    query,
  })

  const [
    vehiclesOkOrReservationsByVehicleTypeTableState,
    setVehiclesOkOrReservationsByVehicleTypeTableState,
  ] = useState({
    page: vehiclesOkOrReservationsByVehicleTypeTablePage,
    limit: 5,
    filters: {
      ...computedFilters,
      groupBy: 'vehicleType',
    },
  })

  const [
    vehiclesOkOrReservationsByVehicleTypeTableData,
    setVehiclesOkOrReservationsByVehicleTypeTableData,
  ] = useState(null)

  // Add state for Vehicles OK or Reservations by Constructor
  const [
    vehiclesOkOrReservationsByConstructorTablePage,
    setVehiclesOkOrReservationsByConstructorTablePage,
  ] = useQueryState({
    paramName: 'vehiclesOkOrReservationsByConstructorPage',
    defaultValue: 0,
    history,
    query,
  })

  const [
    vehiclesOkOrReservationsByConstructorTableState,
    setVehiclesOkOrReservationsByConstructorTableState,
  ] = useState({
    page: vehiclesOkOrReservationsByConstructorTablePage,
    limit: 5,
    filters: {
      ...computedFilters,
      groupBy: 'constructor',
    },
  })

  const [
    vehiclesOkOrReservationsByConstructorTableData,
    setVehiclesOkOrReservationsByConstructorTableData,
  ] = useState(null)

  // Add state for selected view persistence for recontrol
  const [
    vehiclesOkOrReservationsSelectedView,
    setVehiclesOkOrReservationsSelectedView,
  ] = useQueryState({
    paramName: 'vehiclesOkOrReservationsSelectedView',
    defaultValue: 0,
    history,
    query,
  })

  // Add state for Vehicles validated with total control count by Vehicle Type
  const [
    vehiclesValidatedWithTotalControlCountByVehicleTypeTablePage,
    setVehiclesValidatedWithTotalControlCountByVehicleTypeTablePage,
  ] = useQueryState({
    paramName: 'vehiclesValidatedWithTotalControlCountByVehicleTypePage',
    defaultValue: 0,
    history,
    query,
  })

  const [
    vehiclesValidatedWithTotalControlCountByVehicleTypeTableState,
    setVehiclesValidatedWithTotalControlCountByVehicleTypeTableState,
  ] = useState({
    page: vehiclesValidatedWithTotalControlCountByVehicleTypeTablePage,
    limit: 5,
    filters: {
      ...computedFilters,
      groupBy: 'vehicleType',
    },
  })

  const [
    vehiclesValidatedWithTotalControlCountByVehicleTypeTableData,
    setVehiclesValidatedWithTotalControlCountByVehicleTypeTableData,
  ] = useState(null)

  // Add state for Vehicles validated with total control count by Constructor
  const [
    vehiclesValidatedWithTotalControlCountByConstructorTablePage,
    setVehiclesValidatedWithTotalControlCountByConstructorTablePage,
  ] = useQueryState({
    paramName: 'vehiclesValidatedWithTotalControlCountByConstructorPage',
    defaultValue: 0,
    history,
    query,
  })

  const [
    vehiclesValidatedWithTotalControlCountByConstructorTableState,
    setVehiclesValidatedWithTotalControlCountByConstructorTableState,
  ] = useState({
    page: vehiclesValidatedWithTotalControlCountByConstructorTablePage,
    limit: 5,
    filters: {
      ...computedFilters,
      groupBy: 'constructor',
    },
  })

  const [
    vehiclesValidatedWithTotalControlCountByConstructorTableData,
    setVehiclesValidatedWithTotalControlCountByConstructorTableData,
  ] = useState(null)

  // Add state for selected view persistence for recontrol
  const [
    vehiclesValidatedWithTotalControlCountSelectedView,
    setVehiclesValidatedWithTotalControlCountSelectedView,
  ] = useQueryState({
    paramName: 'vehiclesValidatedWithTotalControlCountSelectedView',
    defaultValue: 0,
    history,
    query,
  })

  // Interrupted Controls by Vehicle Type
  const [
    interruptedControlsByVehicleTypeTablePage,
    setInterruptedControlsByVehicleTypeTablePage,
  ] = useQueryState({
    paramName: 'interruptedControlsByVehicleTypePage',
    defaultValue: 0,
    history,
    query,
  })

  const [
    interruptedControlsByVehicleTypeTableState,
    setInterruptedControlsByVehicleTypeTableState,
  ] = useState({
    page: interruptedControlsByVehicleTypeTablePage,
    limit: 5,
    filters: {
      ...computedFilters,
      groupBy: 'vehicleType',
    },
  })

  const [
    interruptedControlsByVehicleTypeTableData,
    setInterruptedControlsByVehicleTypeTableData,
  ] = useState(null)

  // Interrupted Controls by Constructor
  const [
    interruptedControlsByConstructorTablePage,
    setInterruptedControlsByConstructorTablePage,
  ] = useQueryState({
    paramName: 'interruptedControlsByConstructorPage',
    defaultValue: 0,
    history,
    query,
  })

  const [
    interruptedControlsByConstructorTableState,
    setInterruptedControlsByConstructorTableState,
  ] = useState({
    page: interruptedControlsByConstructorTablePage,
    limit: 5,
    filters: {
      ...computedFilters,
      groupBy: 'constructor',
    },
  })

  const [
    interruptedControlsByConstructorTableData,
    setInterruptedControlsByConstructorTableData,
  ] = useState(null)

  const [interruptedControlsSelectedView, setInterruptedControlsSelectedView] =
    useQueryState({
      paramName: 'interruptedControlsSelectedView',
      defaultValue: 0,
      history,
      query,
    })

  useEffect(() => {
    dataProvider
      .get('/dashboard', {})
      .then((response) => setCounts(response.data))
      .catch(() => setFailedLoad(true))
  }, [dataProvider])

  useEffect(() => {
    if (!failedLoad) {
      return
    }

    showNotification('error', t('errors.api-error'))
    setFailedLoad(false)
  }, [showNotification, failedLoad, t])

  useEffect(() => {
    dataProvider
      .getList('/dashboard/data/vehicles-waiting-first-control', {
        range: {
          page: vehiclesWaitingFirstControlByVehicleTypeTableState.page + 1,
          limit: vehiclesWaitingFirstControlByVehicleTypeTableState.limit,
        },
        filters: vehiclesWaitingFirstControlByVehicleTypeTableState.filters,
      })
      .then((response) => {
        setVehiclesWaitingFirstControlByVehicleTypeTableData(response.data)
      })
      .catch(() => showNotification('error', t('errors.api-error')))

    setVehiclesWaitingFirstControlByVehicleTypeTablePage(
      vehiclesWaitingFirstControlByVehicleTypeTableState.page
    )
  }, [vehiclesWaitingFirstControlByVehicleTypeTableState])

  useEffect(() => {
    dataProvider
      .getList('/dashboard/data/vehicles-waiting-first-control', {
        range: {
          page: vehiclesWaitingFirstControlByConstructorTableState.page + 1,
          limit: vehiclesWaitingFirstControlByConstructorTableState.limit,
        },
        filters: vehiclesWaitingFirstControlByConstructorTableState.filters,
      })
      .then((response) => {
        setVehiclesWaitingFirstControlByConstructorTableData(response.data)
      })
      .catch(() => showNotification('error', t('errors.api-error')))

    setVehiclesWaitingFirstControlByConstructorTablePage(
      vehiclesWaitingFirstControlByConstructorTableState.page
    )
  }, [vehiclesWaitingFirstControlByConstructorTableState])

  // Recontrol Vehicle Type Table data fetch
  useEffect(() => {
    dataProvider
      .getList('/dashboard/data/vehicles-waiting-recontrol', {
        range: {
          page: vehiclesWaitingRecontrolByVehicleTypeTableState.page + 1,
          limit: vehiclesWaitingRecontrolByVehicleTypeTableState.limit,
        },
        filters: vehiclesWaitingRecontrolByVehicleTypeTableState.filters,
      })
      .then((response) => {
        setVehiclesWaitingRecontrolByVehicleTypeTableData(response.data)
      })
      .catch(() => showNotification('error', t('errors.api-error')))

    setVehiclesWaitingRecontrolByVehicleTypeTablePage(
      vehiclesWaitingRecontrolByVehicleTypeTableState.page
    )
  }, [vehiclesWaitingRecontrolByVehicleTypeTableState])

  // Recontrol Constructor Table data fetch
  useEffect(() => {
    dataProvider
      .getList('/dashboard/data/vehicles-waiting-recontrol', {
        range: {
          page: vehiclesWaitingRecontrolByConstructorTableState.page + 1,
          limit: vehiclesWaitingRecontrolByConstructorTableState.limit,
        },
        filters: vehiclesWaitingRecontrolByConstructorTableState.filters,
      })
      .then((response) => {
        setVehiclesWaitingRecontrolByConstructorTableData(response.data)
      })
      .catch(() => showNotification('error', t('errors.api-error')))

    setVehiclesWaitingRecontrolByConstructorTablePage(
      vehiclesWaitingRecontrolByConstructorTableState.page
    )
  }, [vehiclesWaitingRecontrolByConstructorTableState])

  // Fetch data for Vehicles OK or Reservations by Vehicle Type
  useEffect(() => {
    dataProvider
      .getList('/dashboard/data/vehicles-ok-or-reservations', {
        range: {
          page: vehiclesOkOrReservationsByVehicleTypeTableState.page + 1,
          limit: vehiclesOkOrReservationsByVehicleTypeTableState.limit,
        },
        filters: vehiclesOkOrReservationsByVehicleTypeTableState.filters,
      })
      .then((response) => {
        setVehiclesOkOrReservationsByVehicleTypeTableData(response.data)
      })
      .catch(() => showNotification('error', t('errors.api-error')))

    setVehiclesOkOrReservationsByVehicleTypeTablePage(
      vehiclesOkOrReservationsByVehicleTypeTableState.page
    )
  }, [vehiclesOkOrReservationsByVehicleTypeTableState])

  // Fetch data for Vehicles OK or Reservations by Constructor
  useEffect(() => {
    dataProvider
      .getList('/dashboard/data/vehicles-ok-or-reservations', {
        range: {
          page: vehiclesOkOrReservationsByConstructorTableState.page + 1,
          limit: vehiclesOkOrReservationsByConstructorTableState.limit,
        },
        filters: vehiclesOkOrReservationsByConstructorTableState.filters,
      })
      .then((response) => {
        setVehiclesOkOrReservationsByConstructorTableData(response.data)
      })
      .catch(() => showNotification('error', t('errors.api-error')))

    setVehiclesOkOrReservationsByConstructorTablePage(
      vehiclesOkOrReservationsByConstructorTableState.page
    )
  }, [vehiclesOkOrReservationsByConstructorTableState])

  // Fetch data for Vehicles validated with total control count by Vehicle Type
  useEffect(() => {
    dataProvider
      .getList('/dashboard/data/vehicles-validated-with-total-control-count', {
        range: {
          page:
            vehiclesValidatedWithTotalControlCountByVehicleTypeTableState.page +
            1,
          limit:
            vehiclesValidatedWithTotalControlCountByVehicleTypeTableState.limit,
        },
        filters:
          vehiclesValidatedWithTotalControlCountByVehicleTypeTableState.filters,
      })
      .then((response) => {
        setVehiclesValidatedWithTotalControlCountByVehicleTypeTableData(
          response.data
        )
      })
      .catch(() => showNotification('error', t('errors.api-error')))

    setVehiclesValidatedWithTotalControlCountByVehicleTypeTablePage(
      vehiclesValidatedWithTotalControlCountByVehicleTypeTableState.page
    )
  }, [vehiclesValidatedWithTotalControlCountByVehicleTypeTableState])

  // Fetch data for Vehicles validated with total control count by Constructor
  useEffect(() => {
    dataProvider
      .getList('/dashboard/data/vehicles-validated-with-total-control-count', {
        range: {
          page:
            vehiclesValidatedWithTotalControlCountByConstructorTableState.page +
            1,
          limit:
            vehiclesValidatedWithTotalControlCountByConstructorTableState.limit,
        },
        filters:
          vehiclesValidatedWithTotalControlCountByConstructorTableState.filters,
      })
      .then((response) => {
        setVehiclesValidatedWithTotalControlCountByConstructorTableData(
          response.data
        )
      })
      .catch(() => showNotification('error', t('errors.api-error')))

    setVehiclesValidatedWithTotalControlCountByConstructorTablePage(
      vehiclesValidatedWithTotalControlCountByConstructorTableState.page
    )
  }, [vehiclesValidatedWithTotalControlCountByConstructorTableState])

  // Fetch data for Interrupted Controls by Vehicle Type
  useEffect(() => {
    dataProvider
      .getList('/dashboard/data/interrupted-controls', {
        range: {
          page: interruptedControlsByVehicleTypeTableState.page + 1,
          limit: interruptedControlsByVehicleTypeTableState.limit,
        },
        filters: interruptedControlsByVehicleTypeTableState.filters,
      })
      .then((response) => {
        setInterruptedControlsByVehicleTypeTableData(response.data)
      })
      .catch(() => showNotification('error', t('errors.api-error')))

    setInterruptedControlsByVehicleTypeTablePage(
      interruptedControlsByVehicleTypeTableState.page
    )
  }, [interruptedControlsByVehicleTypeTableState])

  // Fetch data for Interrupted Controls by Constructor
  useEffect(() => {
    dataProvider
      .getList('/dashboard/data/interrupted-controls', {
        range: {
          page: interruptedControlsByConstructorTableState.page + 1,
          limit: interruptedControlsByConstructorTableState.limit,
        },
        filters: interruptedControlsByConstructorTableState.filters,
      })
      .then((response) => {
        setInterruptedControlsByConstructorTableData(response.data)
      })
      .catch(() => showNotification('error', t('errors.api-error')))

    setInterruptedControlsByConstructorTablePage(
      interruptedControlsByConstructorTableState.page
    )
  }, [interruptedControlsByConstructorTableState])

  const onAddActionsMenuClose = () => {
    setAddActionsAnchorEl({
      ...addActionsAnchorEl,
      anchorEl: null,
      items: [],
    })
  }

  const onAddActions = (currentTarget) => {
    const menuItems = []

    menuItems.push({
      title: t('vehicle-types-list.manual-add'),
      onClick: () => {
        onAddActionsMenuClose()
        setOpenCreateVehicleTypeDialog(true)
      },
    })
    menuItems.push({
      title: t('vehicle-types-list.bulk-add'),
      onClick: () => {
        onAddActionsMenuClose()
        setOpenCreateVehicleTypeBulkDialog(true)
      },
    })

    setAddActionsAnchorEl({
      ...addActionsAnchorEl,
      anchorEl: currentTarget,
      items: menuItems,
    })
  }

  const onAddChassisActions = (currentTarget) => {
    const menuItems = []

    if (canAddChassis) {
      menuItems.push({
        title: t('chassis-list.manual-add'),
        onClick: () => {
          onAddActionsMenuClose()
          setOpenCreateChassisDialog(true)
        },
      })
      menuItems.push({
        title: t('chassis-list.bulk-add'),
        onClick: () => {
          onAddActionsMenuClose()
          setOpenCreateChassisBulkDialog(true)
        },
      })
    }
    if (canAddChassisDocuments) {
      menuItems.push({
        title: t('chassis-list.import-documents'),
        onClick: () => {
          onAddActionsMenuClose()
          setOpenChassisDocumentImportDialog(true)
        },
      })
    }

    if (menuItems.length === 1) {
      menuItems[0].onClick()
      return
    }

    setAddActionsAnchorEl({
      ...addActionsAnchorEl,
      anchorEl: currentTarget,
      items: menuItems,
    })
  }

  function getTableInfoCardsData() {
    // Trigger all endpoints
    setVehiclesWaitingFirstControlByVehicleTypeTableData(null)
    setVehiclesWaitingFirstControlByVehicleTypeTableState({
      page: 0,
      limit: 5,
      filters: {
        ...computedFilters,
        groupBy: 'vehicleType',
      },
    })
    setVehiclesWaitingFirstControlByConstructorTableData(null)
    setVehiclesWaitingFirstControlByConstructorTableState({
      page: 0,
      limit: 5,
      filters: {
        ...computedFilters,
        groupBy: 'constructor',
      },
    })
    setVehiclesWaitingRecontrolByVehicleTypeTableData(null)
    setVehiclesWaitingRecontrolByVehicleTypeTableState({
      page: 0,
      limit: 5,
      filters: {
        ...computedFilters,
        groupBy: 'vehicleType',
      },
    })
    setVehiclesWaitingRecontrolByConstructorTableData(null)
    setVehiclesWaitingRecontrolByConstructorTableState({
      page: 0,
      limit: 5,
      filters: {
        ...computedFilters,
        groupBy: 'constructor',
      },
    })
    setVehiclesOkOrReservationsByVehicleTypeTableData(null)
    setVehiclesOkOrReservationsByVehicleTypeTableState({
      page: 0,
      limit: 5,
      filters: {
        ...computedFilters,
        groupBy: 'vehicleType',
      },
    })
    setVehiclesOkOrReservationsByConstructorTableData(null)
    setVehiclesOkOrReservationsByConstructorTableState({
      page: 0,
      limit: 5,
      filters: {
        ...computedFilters,
        groupBy: 'constructor',
      },
    })
    setVehiclesValidatedWithTotalControlCountByVehicleTypeTableData(null)
    setVehiclesValidatedWithTotalControlCountByVehicleTypeTableState({
      page: 0,
      limit: 5,
      filters: {
        ...computedFilters,
        groupBy: 'vehicleType',
      },
    })
    setVehiclesValidatedWithTotalControlCountByConstructorTableData(null)
    setVehiclesValidatedWithTotalControlCountByConstructorTableState({
      page: 0,
      limit: 5,
      filters: {
        ...computedFilters,
        groupBy: 'constructor',
      },
    })
    setInterruptedControlsByVehicleTypeTableData(null)
    setInterruptedControlsByVehicleTypeTableState({
      page: 0,
      limit: 5,
      filters: {
        ...computedFilters,
        groupBy: 'vehicleType',
      },
    })
    setInterruptedControlsByConstructorTableData(null)
    setInterruptedControlsByConstructorTableState({
      page: 0,
      limit: 5,
      filters: {
        ...computedFilters,
        groupBy: 'constructor',
      },
    })
  }

  const updateFilters = () => {
    getTableInfoCardsData()
  }

  const clearFilters = () => {
    setStartDate(null)
    setEndDate(null)
    setVehicleTypes(null)
    setConstructors(null)
    setMember(null)
    setShowInactive(null)
  }

  const exportCSVForVehiclesWaitingFirstControl = () => {
    if (vehiclesWaitingFirstControlSelectedView === 0) {
      startExport(
        'dashboard-first-control-csv',
        vehiclesWaitingFirstControlByVehicleTypeTableState
      )
    } else {
      startExport(
        'dashboard-first-control-csv',
        vehiclesWaitingFirstControlByConstructorTableState
      )
    }
  }

  const exportCSVForVehiclesWaitingRecontrol = () => {
    if (vehiclesWaitingRecontrolSelectedView === 0) {
      startExport(
        'dashboard-recontrol-csv',
        vehiclesWaitingRecontrolByVehicleTypeTableState
      )
    } else {
      startExport(
        'dashboard-recontrol-csv',
        vehiclesWaitingRecontrolByConstructorTableState
      )
    }
  }

  const exportCSVForVehiclesOkOrReservations = () => {
    if (vehiclesOkOrReservationsSelectedView === 0) {
      startExport(
        'dashboard-ok-reservations-csv',
        vehiclesOkOrReservationsByVehicleTypeTableState
      )
    } else {
      startExport(
        'dashboard-ok-reservations-csv',
        vehiclesOkOrReservationsByConstructorTableState
      )
    }
  }

  const exportCSVForVehiclesValidatedWithTotalControlCount = () => {
    if (vehiclesValidatedWithTotalControlCountSelectedView === 0) {
      startExport(
        'dashboard-validated-count-csv',
        vehiclesValidatedWithTotalControlCountByVehicleTypeTableState
      )
    } else {
      startExport(
        'dashboard-validated-count-csv',
        vehiclesValidatedWithTotalControlCountByConstructorTableState
      )
    }
  }

  const exportCSVForInterruptedControls = () => {
    if (interruptedControlsSelectedView === 0) {
      startExport(
        'dashboard-interrupted-controls-csv',
        interruptedControlsByVehicleTypeTableState
      )
    } else {
      startExport(
        'dashboard-interrupted-controls-csv',
        interruptedControlsByConstructorTableState
      )
    }
  }

  const interruptedControlsByVehicleTypeTableColumns =
    generateInterruptedControlsColumns({ t })

  const interruptedControlsByConstructorTableColumns =
    generateInterruptedControlsColumns({ t })

  const vehiclesWaitingFirstControlByVehicleTypeTableColumns = generateColumns({
    t,
  })

  const vehiclesWaitingFirstControlByConstructorTableColumns = generateColumns({
    t,
  })

  const vehiclesWaitingRecontrolByVehicleTypeTableColumns = generateColumns({
    t,
    needsRecontrol: true,
  })

  const vehiclesWaitingRecontrolByConstructorTableColumns = generateColumns({
    t,
    needsRecontrol: true,
  })

  // Define columns for Vehicles OK or Reservations
  const vehiclesOkOrReservationsByVehicleTypeTableColumns = generateColumns({
    t,
    needsRecontrol: false,
    isValidated: true,
  })

  const vehiclesOkOrReservationsByConstructorTableColumns = generateColumns({
    t,
    needsRecontrol: false,
    isValidated: true,
  })

  // Define columns for Vehicles OK or Reservations
  const vehiclesValidatedWithTotalControlCountByVehicleTypeTableColumns =
    generateValidatedWithTotalControlCountColumns({
      t,
    })

  const vehiclesValidatedWithTotalControlCountByConstructorTableColumns =
    generateValidatedWithTotalControlCountColumns({
      t,
    })

  // still loading
  if (!counts) {
    return <Loading />
  }
  // local
  return (
    <>
      <CreateVehicleTypeBulkDialog
        open={openCreateVehicleTypeBulkDialog}
        onClose={() => {
          setOpenCreateVehicleTypeBulkDialog(false)
        }}
        onCreated={() => {
          setOpenCreateVehicleTypeBulkDialog(false)
        }}
      />
      <CreateChassisBulkDialog
        open={openCreateChassisBulkDialog}
        onClose={() => {
          setOpenCreateChassisBulkDialog(false)
        }}
        onCreated={() => {
          setOpenCreateChassisBulkDialog(false)
        }}
      />
      <ChassisDocumentImportDialog
        open={openChassisDocumentImportDialog}
        onClose={() => {
          setOpenChassisDocumentImportDialog(false)
        }}
        onComplete={() => {
          setOpenChassisDocumentImportDialog(false)
        }}
      />
      <CreateChassisDialog
        open={openCreateChassisDialog}
        onClose={() => setOpenCreateChassisDialog(false)}
        onCreated={() => setOpenCreateChassisDialog(false)}
      />
      <CreateVehicleTypeDialog
        open={openCreateVehicleTypeDialog}
        onClose={() => setOpenCreateVehicleTypeDialog(false)}
        onCreated={() => setOpenCreateVehicleTypeDialog(false)}
      />
      <GeneratePVAdmissionDialog
        open={openGeneratePVAdmissionDialog}
        onClose={() => setOpenGeneratePVAdmissionDialog(false)}
        onCreated={() => setOpenGeneratePVAdmissionDialog(false)}
      />
      <CreateControlDialog
        open={openCreateControlDialog}
        onClose={() => {
          setOpenCreateControlDialog(false)
          setCreateControlDefaultDate(null)
        }}
        onCreated={() => {
          setOpenCreateControlDialog(false)
          setCreateControlDefaultDate(null)
        }}
        defaultDate={createControlDefaultDate}
      />
      <Menu
        keepMounted
        anchorEl={addActionsAnchorEl.anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={onAddActionsMenuClose}
        open={Boolean(addActionsAnchorEl.anchorEl)}
      >
        {addActionsAnchorEl.items.map((item) => (
          <MenuItem key={item.title} onClick={item.onClick}>
            {item.title}
          </MenuItem>
        ))}
      </Menu>
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item xs={12} sm={4}>
          <PageTitle title={t('dashboard-view.title')} />
        </Grid>
        <Grid item container sm={8} justifyContent="flex-end">
          {(canAddChassis || canAddChassisDocuments) && (
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              fullWidth={fullWidth}
              onClick={(event) => onAddChassisActions(event.currentTarget)}
            >
              {addChassisButtonLabel()}
            </Button>
          )}
          {hasPermission(Permissions.PAGE_DASHBOARD_ADD_VEHICLE_TYPE, user) && (
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              fullWidth={fullWidth}
              onClick={(event) => onAddActions(event.currentTarget)}
            >
              {t('dashboard-view.add-vehicle-type')}
            </Button>
          )}
          {hasPermission(
            Permissions.PAGE_CONTROLS_ACCESS_PV_ADMISSION,
            user
          ) && (
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              fullWidth={fullWidth}
              onClick={() => setOpenGeneratePVAdmissionDialog(true)}
            >
              {t('dashboard-view.generate-pv-admission')}
            </Button>
          )}
        </Grid>
      </Grid>

      <Grid
        container
        spacing={4}
        justifyContent="flex-start"
        className={classes.cardsContainer}
      >
        <Grid item lg={3} md={4} sm={6} xs={12}>
          <Widget
            title={t('dashboard-view.vehicles-to-check')}
            upperTitle
            bodyClass={classes.fullHeightBody}
            className={classes.card}
            disableWidgetMenu
          >
            <div className={classes.visitsNumberContainer}>
              <Typography variant="h3">{counts.controlCount}</Typography>
            </div>
          </Widget>
        </Grid>
        <Grid item lg={3} md={4} sm={6} xs={12}>
          <Widget
            title={t('dashboard-view.vehicles-to-recheck')}
            upperTitle
            bodyClass={classes.fullHeightBody}
            className={classes.card}
            disableWidgetMenu
          >
            <div className={classes.visitsNumberContainer}>
              <Typography variant="h3">{counts.controlAgainCount}</Typography>
            </div>
          </Widget>
        </Grid>
      </Grid>
      <Grid container item className={classes.filtersContainer}>
        <Grid item>
          <Typography variant="h6" className={classes.filterContainerTitleText}>
            {t('common.filterZone')}
          </Typography>
        </Grid>
        <Grid
          item
          container
          alignItems="center"
          justifyContent="flex-start"
          spacing={1}
        >
          <Grid container item xs={12} md={6} alignItems="flex-end">
            <Grid item xs={12} md={5}>
              <DatePicker
                id="startDate"
                value={startDate}
                onChange={setStartDate}
                label={t('common.startDate')}
              />
            </Grid>
            <Grid item md={1}>
              <Typography variant="subtitle1" align="center">
                Au
              </Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <DatePicker
                id="endDate"
                value={endDate}
                onChange={setEndDate}
                label={t('common.endDate')}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} md={3} marginLeft={-4}>
            <AutoCompleteInput
              multiple
              onChange={setVehicleTypes}
              label={t('common.vehicleType')}
              resource="vehicle-type/autocomplete"
              value={vehicleTypes}
              query={{ includeDisabled: showInactive === '1' }}
              canDelete
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <AutoCompleteInput
              multiple
              onChange={setConstructors}
              label={t('common.constructeurs')}
              resource="constructors/autocomplete"
              value={constructors}
              canDelete
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <AutoCompleteInput
              onChange={setMember}
              label={t('common.member')}
              resource="members/autocomplete"
              value={member}
              canDelete
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  onChange={(event) =>
                    setShowInactive(event.target.checked ? '1' : null)
                  }
                  checked={showInactive === '1'}
                />
              }
              label={t('common.show-deactivated-vehicle-types')}
            />
          </Grid>
          <Grid
            item
            container
            spacing={2}
            justifyContent="flex-end"
            className={classes.statisticsActionsContainer}
            marginTop={2}
          >
            <Grid item xs={12} md={2} xl={1}>
              <Button
                color="secondary"
                variant="contained"
                onClick={clearFilters}
                fullWidth
              >
                {t('common.clean')}
              </Button>
            </Grid>
            <Grid item xs={12} md={2} xl={1}>
              <Button
                color="primary"
                variant="contained"
                fullWidth
                onClick={updateFilters}
              >
                {t('common.search')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid
        container
        spacing={2}
        justifyContent="flex-start"
        className={classes.cardsContainer}
      >
        <Grid item lg={6} md={8} sm={10} xs={12}>
          <BFTableInfoCard
            title={t(
              'dashboard-view.data.vehicles-waiting-first-control.title'
            )}
            data={[
              vehiclesWaitingFirstControlByVehicleTypeTableData,
              vehiclesWaitingFirstControlByConstructorTableData,
            ]}
            state={[
              vehiclesWaitingFirstControlByVehicleTypeTableState,
              vehiclesWaitingFirstControlByConstructorTableState,
            ]}
            columns={[
              vehiclesWaitingFirstControlByVehicleTypeTableColumns,
              vehiclesWaitingFirstControlByConstructorTableColumns,
            ]}
            buttons={[
              t(
                'dashboard-view.data.vehicles-waiting-first-control.vehicle-type-button'
              ),
              t(
                'dashboard-view.data.vehicles-waiting-first-control.constructor-button'
              ),
            ]}
            backgroundColor="#FFFFFF"
            mainColor={theme.palette.primary.main}
            minHeight={450}
            isLoading={
              !vehiclesWaitingFirstControlByVehicleTypeTableData ||
              !vehiclesWaitingFirstControlByConstructorTableData
            }
            initialView={vehiclesWaitingFirstControlSelectedView}
            onViewChange={(view) =>
              setVehiclesWaitingFirstControlSelectedView(view)
            }
            onChangePage={(currentView, currentPage) => {
              if (currentView === 0) {
                setVehiclesWaitingFirstControlByVehicleTypeTableState(
                  (prevState) => ({
                    ...prevState,
                    page: currentPage,
                  })
                )
              } else {
                setVehiclesWaitingFirstControlByConstructorTableState(
                  (prevState) => ({
                    ...prevState,
                    page: currentPage,
                  })
                )
              }
            }}
            onSelectRow={(currentView, row) => {
              if (row && row.id) {
                history.push(`/chassis/detail/${row.id}`)
              }
            }}
            noDataMessage={t('tables.body.noMatch')}
            onExportCsv={exportCSVForVehiclesWaitingFirstControl}
          />
        </Grid>

        {/* New BFTableInfoCard for Recontrol */}
        <Grid item lg={6} md={8} sm={10} xs={12}>
          <BFTableInfoCard
            title={t('dashboard-view.data.vehicles-waiting-recontrol.title')}
            data={[
              vehiclesWaitingRecontrolByVehicleTypeTableData,
              vehiclesWaitingRecontrolByConstructorTableData,
            ]}
            state={[
              vehiclesWaitingRecontrolByVehicleTypeTableState,
              vehiclesWaitingRecontrolByConstructorTableState,
            ]}
            columns={[
              vehiclesWaitingRecontrolByVehicleTypeTableColumns,
              vehiclesWaitingRecontrolByConstructorTableColumns,
            ]}
            buttons={[
              t(
                'dashboard-view.data.vehicles-waiting-recontrol.vehicle-type-button'
              ),
              t(
                'dashboard-view.data.vehicles-waiting-recontrol.constructor-button'
              ),
            ]}
            backgroundColor="#FFFFFF"
            mainColor={theme.palette.primary.main}
            minHeight={450}
            isLoading={
              !vehiclesWaitingRecontrolByVehicleTypeTableData ||
              !vehiclesWaitingRecontrolByConstructorTableData
            }
            initialView={vehiclesWaitingRecontrolSelectedView}
            onViewChange={(view) =>
              setVehiclesWaitingRecontrolSelectedView(view)
            }
            onChangePage={(currentView, currentPage) => {
              if (currentView === 0) {
                setVehiclesWaitingRecontrolByVehicleTypeTableState(
                  (prevState) => ({
                    ...prevState,
                    page: currentPage,
                  })
                )
              } else {
                setVehiclesWaitingRecontrolByConstructorTableState(
                  (prevState) => ({
                    ...prevState,
                    page: currentPage,
                  })
                )
              }
            }}
            onSelectRow={(currentView, row) => {
              if (row && row.id) {
                history.push(`/chassis/detail/${row.id}`)
              }
            }}
            noDataMessage={t('tables.body.noMatch')}
            onExportCsv={exportCSVForVehiclesWaitingRecontrol}
          />
        </Grid>

        {/* New BFTableInfoCard for Vehicles OK or Reservations */}
        <Grid item lg={6} md={8} sm={10} xs={12}>
          <BFTableInfoCard
            title={t('dashboard-view.data.vehicles-ok-or-reservations.title')}
            data={[
              vehiclesOkOrReservationsByVehicleTypeTableData,
              vehiclesOkOrReservationsByConstructorTableData,
            ]}
            state={[
              vehiclesOkOrReservationsByVehicleTypeTableState,
              vehiclesOkOrReservationsByConstructorTableState,
            ]}
            columns={[
              vehiclesOkOrReservationsByVehicleTypeTableColumns,
              vehiclesOkOrReservationsByConstructorTableColumns,
            ]}
            buttons={[
              t(
                'dashboard-view.data.vehicles-ok-or-reservations.vehicle-type-button'
              ),
              t(
                'dashboard-view.data.vehicles-ok-or-reservations.constructor-button'
              ),
            ]}
            backgroundColor="#FFFFFF"
            mainColor={theme.palette.primary.main}
            minHeight={450}
            isLoading={
              !vehiclesOkOrReservationsByVehicleTypeTableData ||
              !vehiclesOkOrReservationsByConstructorTableData
            }
            initialView={vehiclesOkOrReservationsSelectedView}
            onViewChange={(view) =>
              setVehiclesOkOrReservationsSelectedView(view)
            }
            onChangePage={(currentView, currentPage) => {
              if (currentView === 0) {
                setVehiclesOkOrReservationsByVehicleTypeTableState(
                  (prevState) => ({
                    ...prevState,
                    page: currentPage,
                  })
                )
              } else {
                setVehiclesOkOrReservationsByConstructorTableState(
                  (prevState) => ({
                    ...prevState,
                    page: currentPage,
                  })
                )
              }
            }}
            onSelectRow={(currentView, row) => {
              if (row && row.id) {
                history.push(`/chassis/detail/${row.id}`)
              }
            }}
            noDataMessage={t('tables.body.noMatch')}
            onExportCsv={exportCSVForVehiclesOkOrReservations}
          />
        </Grid>
        {/* New BFTableInfoCard for Vehicles Validated With Total Control Count*/}
        <Grid item lg={6} md={8} sm={10} xs={12}>
          <BFTableInfoCard
            title={t(
              'dashboard-view.data.vehicles-validated-with-total-count.title'
            )}
            data={[
              vehiclesValidatedWithTotalControlCountByVehicleTypeTableData,
              vehiclesValidatedWithTotalControlCountByConstructorTableData,
            ]}
            state={[
              vehiclesValidatedWithTotalControlCountByVehicleTypeTableState,
              vehiclesValidatedWithTotalControlCountByConstructorTableState,
            ]}
            columns={[
              vehiclesValidatedWithTotalControlCountByVehicleTypeTableColumns,
              vehiclesValidatedWithTotalControlCountByConstructorTableColumns,
            ]}
            buttons={[
              t(
                'dashboard-view.data.vehicles-ok-or-reservations.vehicle-type-button'
              ),
              t(
                'dashboard-view.data.vehicles-ok-or-reservations.constructor-button'
              ),
            ]}
            backgroundColor="#FFFFFF"
            mainColor={theme.palette.primary.main}
            minHeight={450}
            isLoading={
              !vehiclesValidatedWithTotalControlCountByVehicleTypeTableData ||
              !vehiclesValidatedWithTotalControlCountByConstructorTableData
            }
            initialView={vehiclesValidatedWithTotalControlCountSelectedView}
            onViewChange={(view) =>
              setVehiclesValidatedWithTotalControlCountSelectedView(view)
            }
            onChangePage={(currentView, currentPage) => {
              if (currentView === 0) {
                setVehiclesValidatedWithTotalControlCountByVehicleTypeTableState(
                  (prevState) => ({
                    ...prevState,
                    page: currentPage,
                  })
                )
              } else {
                setVehiclesValidatedWithTotalControlCountByConstructorTableState(
                  (prevState) => ({
                    ...prevState,
                    page: currentPage,
                  })
                )
              }
            }}
            onSelectRow={(currentView, row) => {
              if (row && row.vehicleTypeId) {
                history.push(`/vehicle-types/detail/${row.vehicleTypeId}`)
              }
            }}
            noDataMessage={t('tables.body.noMatch')}
            onExportCsv={exportCSVForVehiclesValidatedWithTotalControlCount}
          />
        </Grid>
        {/* Interrupted Controls Table */}
        <Grid item lg={6} md={8} sm={10} xs={12}>
          <BFTableInfoCard
            title={t('dashboard-view.data.interrupted-controls.title')}
            data={[
              interruptedControlsByVehicleTypeTableData,
              interruptedControlsByConstructorTableData,
            ]}
            state={[
              interruptedControlsByVehicleTypeTableState,
              interruptedControlsByConstructorTableState,
            ]}
            columns={[
              interruptedControlsByVehicleTypeTableColumns,
              interruptedControlsByConstructorTableColumns,
            ]}
            buttons={[
              t('dashboard-view.data.interrupted-controls.vehicle-type-button'),
              t('dashboard-view.data.interrupted-controls.constructor-button'),
            ]}
            backgroundColor="#FFFFFF"
            mainColor={theme.palette.primary.main}
            minHeight={450}
            isLoading={
              !interruptedControlsByVehicleTypeTableData ||
              !interruptedControlsByConstructorTableData
            }
            initialView={interruptedControlsSelectedView}
            onViewChange={(view) => setInterruptedControlsSelectedView(view)}
            onChangePage={(currentView, currentPage) => {
              if (currentView === 0) {
                setInterruptedControlsByVehicleTypeTableState((prevState) => ({
                  ...prevState,
                  page: currentPage,
                }))
              } else {
                setInterruptedControlsByConstructorTableState((prevState) => ({
                  ...prevState,
                  page: currentPage,
                }))
              }
            }}
            onSelectRow={(currentView, row) => {
              if (row && row.id) {
                history.push(`/controls/detail/${row.id}`)
              }
            }}
            noDataMessage={t('tables.body.noMatch')}
            onExportCsv={exportCSVForInterruptedControls}
          />
        </Grid>
      </Grid>

      {/* Controls Calendar */}
      <Grid
        container
        spacing={2}
        className={classes.cardsContainer}
        sx={{ marginBottom: 3 }}
      >
        <Grid item xs={12}>
          <InspectionsCalendar
            title={t('dashboard-view.controls-calendar.title')}
            titleColor={theme.palette.text.primary}
            titleFontSize="18px"
            primaryColor={theme.palette.primary.main}
            showControllerFilter={hasPermission(
              Permissions.PAGE_DASHBOARD_CALENDAR_SEE_ALL_EVENTS,
              user
            )}
            canRescheduleEvents={hasPermission(
              Permissions.PAGE_DASHBOARD_CALENDAR_RESCHEDULE_EVENTS,
              user
            )}
            canCancelOverdueEvents={hasPermission(
              Permissions.PAGE_DASHBOARD_CALENDAR_CANCEL_OVERDUE_EVENTS,
              user
            )}
            canCreateControl={hasPermission(
              Permissions.PAGE_DASHBOARD_CALENDAR_CREATE_CONTROL,
              user
            )}
            canAssignController={hasPermission(
              Permissions.PAGE_DASHBOARD_CALENDAR_ASSIGN_CONTROLLER,
              user
            )}
            defaultView={calendarView}
            onViewChange={setCalendarView}
            defaultDate={calendarDate}
            onDateChange={setCalendarDate}
            defaultSelectedControllers={calendarControllers}
            onSelectedControllersChange={setCalendarControllers}
            onCreateControl={(date) => {
              setCreateControlDefaultDate(date)
              setOpenCreateControlDialog(true)
            }}
            fetchEventsFunction={async (params) => {
              const response = await dataProvider.get('/calendar/events', {
                query: params,
              })
              return response.data
            }}
            fetchControllersFunction={async ({ includeHidden } = {}) => {
              const query = includeHidden ? { includeHidden: 'true' } : {}
              const response = await dataProvider.get('/calendar/controllers', {
                query,
              })
              return response.data
            }}
            updateControllerColorFunction={async (userId, color) => {
              const response = await dataProvider.patch(
                `/calendar/controllers/${userId}/color`,
                {
                  data: { color },
                }
              )
              return response.data
            }}
            updateControllerVisibilityFunction={async (userId, visible) => {
              const response = await dataProvider.patch(
                `/calendar/controllers/${userId}/visibility`,
                {
                  data: { visible },
                }
              )
              return response.data
            }}
            updateEventDateFunction={async (type, controlId, newDate) => {
              const response = await dataProvider.patch(
                `/calendar/events/${type}/${controlId}/date`,
                {
                  data: { newDate },
                }
              )
              return response.data
            }}
            cancelEventFunction={async (type, controlId) => {
              const response = await dataProvider.patch(
                `/calendar/events/${type}/${controlId}/cancel`,
                { data: {} }
              )
              return response.data
            }}
            assignControllerFunction={async (type, controlId, controllerId) => {
              const response = await dataProvider.patch(
                `/calendar/events/${type}/${controlId}/assign`,
                { data: { controllerId } }
              )
              return response.data
            }}
            getEventTitle={(event) => {
              const nParc = event.chassis?.nParc || ''
              const constructor = event.vehicleType?.constructor?.name || ''
              if (nParc && constructor) return `| ${nParc} - ${constructor}`
              return `| ${nParc || constructor || t('calendar.unassigned')}`
            }}
            exportCSVFunction={(
              events,
              {
                t,
                selectedDate,
                isEventPast,
                isEventCompleted,
                CONTROL_STATUS,
                moment,
              }
            ) => {
              const getConformityLabel = (conformity) => {
                if (conformity === true || conformity === 1)
                  return t('conformity.valid', { defaultValue: 'Valid' })
                if (conformity === false || conformity === 0)
                  return t('conformity.invalid', { defaultValue: 'Invalid' })
                return t('conformity.waiting-result', {
                  defaultValue: 'Waiting',
                })
              }

              const getStatusLabel = (event) => {
                const isCanceled = event.status === CONTROL_STATUS.CANCELED
                if (isCanceled) return t('calendar.status.canceled')
                const eventDate = event.dateTime || event.date
                const isOverdue =
                  isEventPast(eventDate) && !isEventCompleted(event.conformity)
                if (isOverdue) return t('calendar.status.late')
                return ''
              }

              const csvContent = [
                [
                  t('calendar.export.date'),
                  t('calendar.export.controller'),
                  t('calendar.export.constructor'),
                  t('calendar.export.location'),
                  t('calendar.export.chassis-number'),
                  t('calendar.export.park-number'),
                  t('calendar.export.vehicle-type'),
                  t('calendar.export.conformity'),
                  t('calendar.export.status'),
                ].join(','),
                ...events.map((event) => {
                  const controllerName = event.controller
                    ? `${event.controller.firstName} ${event.controller.lastName}`
                    : t('calendar.unassigned')
                  const constructorName =
                    event.vehicleType?.constructor?.name || 'N/A'
                  const locationName = event.location?.name || 'N/A'
                  const dateTime = event.dateTime
                    ? moment(event.dateTime).format('DD/MM/YYYY HH:mm')
                    : moment(event.date).format('DD/MM/YYYY')
                  const chassisNumber = event.chassis?.nChassis || 'N/A'
                  const parkNumber = event.chassis?.nParc || 'N/A'
                  const vehicleType = event.vehicleType?.title || 'N/A'
                  const conformity = getConformityLabel(event.conformity)
                  const status = getStatusLabel(event)
                  return [
                    dateTime,
                    controllerName,
                    constructorName,
                    locationName,
                    chassisNumber,
                    parkNumber,
                    vehicleType,
                    conformity,
                    status,
                  ]
                    .map((field) => `"${String(field).replace(/"/g, '""')}"`)
                    .join(',')
                }),
              ].join('\n')

              const blob = new Blob([csvContent], {
                type: 'text/csv;charset=utf-8;',
              })
              const link = document.createElement('a')
              const url = URL.createObjectURL(blob)
              link.setAttribute('href', url)
              link.setAttribute(
                'download',
                `controls_${moment(selectedDate).format('YYYY-MM-DD')}.csv`
              )
              link.style.visibility = 'hidden'
              document.body.appendChild(link)
              link.click()
              document.body.removeChild(link)
            }}
            onRefreshRef={(fn) => {
              calendarRefreshRef.current = fn
            }}
            onImportCalendar={() => setImportDialogOpen(true)}
            onExportTemplate={() => {
              setExportTemplateConstructor(null)
              setExportTemplateDialogOpen(true)
            }}
            onError={(message) => showNotification('error', message)}
            height="700px"
            itemLabelSingular={t(
              'dashboard-view.controls-calendar.vehicle-singular'
            )}
            itemLabelPlural={t(
              'dashboard-view.controls-calendar.vehicle-plural'
            )}
            memberId={member?.id}
            onControlClick={(event) => {
              if (event.controlId) {
                const path =
                  event.type === 'membercontrol'
                    ? `/member-controls/detail/${event.controlId}`
                    : `/controls/detail/${event.controlId}`
                window.open(path, '_blank')
              }
            }}
            renderEventListItem={(
              event,
              { controllerColors, t, UNASSIGNED_COLOR }
            ) => ({
              primary: (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: 1,
                      backgroundColor: event.controller
                        ? controllerColors[event.controller.id] ||
                          UNASSIGNED_COLOR
                        : UNASSIGNED_COLOR,
                    }}
                  />
                  <Typography variant="subtitle1">
                    {event.controller
                      ? `${event.controller.firstName} ${event.controller.lastName}`
                      : t('calendar.unassigned')}
                  </Typography>
                </Box>
              ),
              secondary: (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t('calendar.day-events.constructor')}:</strong>{' '}
                    {event.vehicleType?.constructor?.name || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t('calendar.day-events.location')}:</strong>{' '}
                    {event.location?.name || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t('common.nParc')}:</strong>{' '}
                    {event.chassis?.nParc || 'N/A'}
                  </Typography>
                </Box>
              ),
            })}
            renderControlDetails={(
              control,
              {
                onClose,
                onViewFullDetails,
                onCancel,
                onAssign,
                controllers,
                controllerColors,
                mainColor,
                inline,
                isOverdue,
              }
            ) => (
              <ControlDetailsPreview
                control={control}
                onClose={onClose}
                onViewFullDetails={onViewFullDetails}
                onCancel={onCancel}
                onAssign={onAssign}
                controllers={controllers}
                controllerColors={controllerColors}
                mainColor={mainColor}
                inline={inline}
                isOverdue={isOverdue}
              />
            )}
            calendarSx={{
              '& .fc': {
                fontSize: '0.85em',
              },
              '& .fc-event': {
                fontSize: '0.85em',
              },
              '& .fc-daygrid-day-number': {
                fontSize: '0.9em',
              },
              '& .fc-col-header-cell-cushion': {
                fontSize: '0.85em',
              },
              '& .fc-timegrid-slot-label': {
                fontSize: '0.8em',
              },
            }}
          />
        </Grid>
      </Grid>

      {/* Calendar import dialog */}
      <CalendarImport
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onCreated={() => {
          setImportDialogOpen(false)
          calendarRefreshRef.current?.()
        }}
      />

      {/* Export template dialog */}
      <BaseDialog
        open={exportTemplateDialogOpen}
        onClose={() => {
          setExportTemplateDialogOpen(false)
          setExportTemplateLoading(false)
        }}
        title={t('calendar.export-template')}
        width="xs"
        hideActions
      >
        <Box sx={{ pt: 1 }}>
          <AutoCompleteInput
            onChange={setExportTemplateConstructor}
            label={t('calendar-import.upload-select-constructor')}
            resource="constructors/autocomplete"
            value={exportTemplateConstructor}
            canDelete
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            disabled={!exportTemplateConstructor || exportTemplateLoading}
            onClick={async () => {
              if (exportTemplateConstructor) {
                setExportTemplateLoading(true)
                try {
                  await dataProvider.calendar.exportTemplate(
                    exportTemplateConstructor.id
                  )
                  setExportTemplateDialogOpen(false)
                } catch (err) {
                  showNotification('error', t('calendar.export-template-error'))
                } finally {
                  setExportTemplateLoading(false)
                }
              }
            }}
          >
            {t('common.confirm')}
          </Button>
        </Box>
      </BaseDialog>
    </>
  )
}
